import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Create an audio context for notification sounds
let audioContext;
let notificationBuffer;

export async function playNotificationSound(variant = "default") {
  try {
    if (typeof window === "undefined") return;

    // Initialize audio context on first use (needs user interaction)
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Create a subtle notification sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Customize sound based on variant
      const frequencies = {
        default: 440, // A4
        success: 523.25, // C5
        warning: 392, // G4
        destructive: 349.23, // F4
        info: 466.16, // Bb4
      };

      oscillator.frequency.setValueAtTime(
        frequencies[variant] || frequencies.default,
        audioContext.currentTime
      );
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(
        0.05,
        audioContext.currentTime + 0.01
      );
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.15);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    }
  } catch (error) {
    console.warn("Could not play notification sound:", error);
  }
}

/**
 * Fetch user data from Firestore using userId
 * @param {string} userId - The user ID to fetch data for
 * @returns {Promise<Object|null>} User data object or null if not found
 */
export const fetchUserData = async (userId) => {
  if (!userId) {
    console.warn("fetchUserData: No userId provided");
    return null;
  }

  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      console.log("✅ User data fetched for userId:", userId, userData);
      return userData;
    } else {
      console.log("⚠️ No user found for userId:", userId);
      return null;
    }
  } catch (error) {
    console.error("❌ Error fetching user data for userId:", userId, error);
    return null;
  }
};

/**
 * Fetch user data for multiple feedback items efficiently
 * @param {Array} feedbackItems - Array of feedback objects with userId
 * @returns {Promise<Array>} Array of feedback items with userData populated
 */
export const fetchUserDataForFeedback = async (feedbackItems) => {
  if (!feedbackItems || feedbackItems.length === 0) {
    return feedbackItems;
  }

  try {
    // Create a map of unique userIds to avoid duplicate fetches
    const uniqueUserIds = [
      ...new Set(feedbackItems.map((item) => item.userId).filter(Boolean)),
    ];
    console.log(
      "🔄 Fetching user data for",
      uniqueUserIds.length,
      "unique users"
    );

    // Fetch user data for all unique users
    const userDataPromises = uniqueUserIds.map((userId) =>
      fetchUserData(userId)
    );
    const userDataResults = await Promise.all(userDataPromises);

    // Create a map of userId to userData
    const userDataMap = {};
    uniqueUserIds.forEach((userId, index) => {
      userDataMap[userId] = userDataResults[index];
    });

    // Attach user data to feedback items
    const feedbackWithUserData = feedbackItems.map((item) => ({
      ...item,
      userData: userDataMap[item.userId] || null,
    }));

    console.log(
      "✅ User data attached to",
      feedbackWithUserData.length,
      "feedback items"
    );
    return feedbackWithUserData;
  } catch (error) {
    console.error("❌ Error fetching user data for feedback:", error);
    return feedbackItems;
  }
};

/**
 * Send email notification when feedback status changes
 * @param {string} userEmail - The user's email address
 * @param {string} feedbackComment - The feedback comment
 * @param {string} oldStatus - Previous status
 * @param {string} newStatus - New status
 * @param {string} assignedTo - Who it's assigned to (optional)
 */
export const sendStatusUpdateEmail = (
  userEmail,
  feedbackComment,
  oldStatus,
  newStatus,
  assignedTo = null
) => {
  if (!userEmail || userEmail === "No email") {
    console.log("⚠️ No valid email to send notification to");
    return;
  }

  const subject = `Feedback Update - ${
    newStatus.charAt(0).toUpperCase() + newStatus.slice(1)
  }`;

  let body = `Hi there,\n\n`;
  body += `Your feedback has been updated:\n\n`;
  body += `Feedback: "${feedbackComment.substring(0, 100)}${
    feedbackComment.length > 100 ? "..." : ""
  }"\n`;
  body += `Status: ${oldStatus} → ${newStatus}\n`;

  if (assignedTo) {
    body += `Assigned to: ${assignedTo}\n`;
  }

  body += `\nWe'll keep you updated on any further changes.\n\n`;
  body += `Best regards,\nThe Resume Builder Team`;

  const mailtoLink = `mailto:${userEmail}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;

  // Open email client
  window.open(mailtoLink, "_blank");

  console.log("📧 Email notification prepared for:", userEmail);
};

/**
 * Get status change message for notifications
 * @param {string} oldStatus - Previous status
 * @param {string} newStatus - New status
 * @returns {string} Human readable status change message
 */
export const getStatusChangeMessage = (oldStatus, newStatus) => {
  const statusMessages = {
    new: "marked as new",
    assigned: "assigned to team member",
    in_progress: "marked as in progress",
    resolved: "marked as resolved",
    wont_fix: "marked as won't fix",
  };

  return statusMessages[newStatus] || `updated to ${newStatus}`;
};
